import { useTranslation } from '@dreamstack/i18n'
import { ModalCloseButton } from '@dreamstack/simple-components/src/lib/ModalCloseButton'
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import Image from 'next/image'
import type { RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'

type Props = {
  open: boolean
  handleClose: () => void
  title: string
  subtitle?: string
  steps?:
    | {
        step: number
        total: number
      }
    | string
  imageSrc: string
  children: React.ReactNode
}

export const DialogWithImage = ({
  open,
  handleClose,
  title,
  subtitle,
  imageSrc,
  children,
  steps,
}: Props) => {
  const t = useTranslation()
  const modalRef = useRef<HTMLDivElement>(null)
  const higherThanViewport = useHigherThanViewport({ ref: modalRef })

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="fixed z-20 overflow-y-auto flex w-full h-screen"
      style={
        higherThanViewport
          ? {
              top: 0,
              alignItems: 'start',
              justifyContent: 'center',
            }
          : {
              top: 0,
              bottom: 0,
              alignItems: 'center',
              justifyContent: 'center',
            }
      }
    >
      <DialogBackdrop className="fixed inset-0 backdrop-blur-sm bg-accentroGray-300" />
      <DialogPanel
        className="relative bg-accentroWhite-full flex w-full max-w-[96vw] md:max-w-[784px] ml:max-w-[1020px] my-px-12"
        ref={modalRef}
      >
        <div className="relative w-0 sm:w-[222px] ml:w-[340px]">
          <Image
            src={imageSrc}
            fill
            className="object-cover"
            alt="fake"
          />
        </div>
        <div className="flex-1 bg-white p-px-24 sm:p-px-40 ml:p-px-64">
          <ModalCloseButton handleClose={handleClose} />
          <div className="flex flex-col gap-px-40 ml:gap-px-48">
            {/* Title */}
            <div className="flex flex-col gap-px-8 text-accentroGray-full pt-px-24 sm:p-0">
              {!!steps && (
                <p className="text-sm font-semibold">
                  {typeof steps === 'string'
                    ? steps
                    : t('accentro:onboardingModal.steps', { ...steps })}
                </p>
              )}
              <p className="text-2xl md:text-3xl ml:text-4xl font-semibold">
                {title}
              </p>
              {!!subtitle && (
                <p className="text-base ml:text-xl font-semibold">{subtitle}</p>
              )}
            </div>
            {children}
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  )
}

const useHigherThanViewport = ({
  ref,
}: {
  ref: RefObject<HTMLDivElement | null>
}) => {
  const [biggerThanVP, setBiggerThanVP] = useState(false)

  useEffect(() => {
    const handleResize = () =>
      setBiggerThanVP(
        ref.current !== null && ref.current.clientHeight > window.innerHeight
      )
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [ref])

  return biggerThanVP
}
